import React, { useCallback, useEffect, useState, useRef } from 'react';
import OChartCard from '../../../../../Components/OElements/OChartCard';
import { Select } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { getConsumptionsSummary, getCostPPEByEstablishment, getEstablishmentList } from '../../../../../slices/thunks';
import { createSelector } from 'reselect';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { constants } from '../../../../../config';
import { ECHARTS_COLOR_PALETTE } from '../../../../../Components/Hooks/useColorArrayHooks';
import { calculatePointSize } from './utils/mapUtils';

// Set Mapbox access token
mapboxgl.accessToken = constants.REACT_APP_MAPBOX;

export const CostPPECenterManagementMap = ({
    title,
    defaultFilters = {
        option: 'semana',
    },
    minHeight,
    maxHeight,
    companyId = null,
}) => {
    const dispatch = useDispatch();
    const [selectedDate, setSelectedDate] = useState(defaultFilters.option);
    const mapContainer = useRef(null);
    const mapInstance = useRef(null);
    const boundsRef = useRef(new mapboxgl.LngLatBounds());

    // const selectorConsumptions = createSelector(
    //     state => state.FuelSupply,
    //     data => ({ info: data.consumptionsSummaryManagement || null })
    // );
    // const { info: infoConsumptions } = useSelector(selectorConsumptions);

    // Layout mode selector for map theme
    const selectLayoutState = state => state.Layout;
    const selectLayoutProperties = createSelector(selectLayoutState, layout => ({
        layoutModeType: layout.layoutModeType,
    }));
    const { layoutModeType } = useSelector(selectLayoutProperties);

    const [infoConsumptions, setInfoConsumptions] = useState([]);

    const fetchConsumptionsData = useCallback(async (filter, company_id) => {
        const response = await dispatch(
            getCostPPEByEstablishment({
                // NO establishment_id here - this is for management view
                filter: filter,
                group: 'establecimiento', // Group by establishment
                company_id: company_id, // Add company filter
            })
        ).unwrap();

        setInfoConsumptions(response);
    }, [dispatch])

    useEffect(() => {
        fetchConsumptionsData(selectedDate, companyId);
    }, [selectedDate, companyId, fetchConsumptionsData]);

    const [infoEstablishments, setInfoEstablishments] = useState([]);

    const fetchEstablishmentsData = useCallback(async () => {
        const response = await dispatch(
            getEstablishmentList({
                // // NO establishment_id here - this is for management view
                // filter: filter,
                // group: 'establecimiento', // Group by establishment
                // company_id: company_id, // Add company filter
            })
        ).unwrap();

        setInfoEstablishments(response);
    }, [dispatch])

    useEffect(() => {
        fetchConsumptionsData(selectedDate, companyId);
        fetchEstablishmentsData();
    }, [selectedDate, companyId, fetchConsumptionsData]);

    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [hasValidData, setHasValidData] = useState(false);

    useEffect(() => {
        if (Array.isArray(infoConsumptions?.data)) {
            setHasLoadedOnce(true);
            if (infoConsumptions.data.length > 0) {
                setHasValidData(true);
            }
        }
    }, [infoConsumptions?.data]);

    // Only show empty state if we've loaded and confirmed there's no data
    const noData = hasLoadedOnce && !infoConsumptions?.data?.length;

    // Process establishment data for map
    const processEstablishmentData = useCallback(() => {
        if (!infoConsumptions?.length || !Array.isArray(infoConsumptions.length)) {
            return [];
        }

        // Get all establishments from all periods
        const allEstablishments = [];
        infoConsumptions.data.forEach(establishment => {
          
                period.establishments.forEach(establishment => {
                    // Check if establishment already exists
                    const existingIndex = allEstablishments.findIndex(
                        item =>
                            item._id === establishment._id
                    );

                    if (existingIndex >= 0) {
                        // Add consumption to existing establishment
                        allEstablishments[existingIndex].total_consumption = establishment.total_price;
                        allEstablishments[existingIndex].records_count = establishment.count_items;
                    } else {
                        // Add new establishment
                        allEstablishments.push({
                            ...establishment,
                            // periods: [period.date],
                        });
                    }
                });
       
        });

        return allEstablishments;
    }, [infoConsumptions]);

    const establishmentData = processEstablishmentData();

    // Map style based on theme
    const mapStyle =
        layoutModeType === 'dark'
            ? 'mapbox://styles/mapbox/dark-v10'
            : 'mapbox://styles/mapbox/light-v11';

    // Initialize map only once
    useEffect(() => {
        if (!mapContainer.current) {
            return;
        }

        // Clean up existing map
        if (mapInstance.current) {
            mapInstance.current.remove();
            mapInstance.current = null;
        }

        try {
            const map = new mapboxgl.Map({
                container: mapContainer.current,
                style: mapStyle,
                center: [-72.5, -41.5], // Center on Chile region
                zoom: 6,
            });

            mapInstance.current = map;

            // Add data when map loads
            map.on('load', () => {
                if (establishmentData.length > 0) {
                    addEstablishmentsToMap(map, establishmentData);
                }
            });

            // Add error handling
            map.on('error', error => {
                console.error('Map error:', error);
            });
        } catch (error) {
            console.error('Error creating map:', error);
        }

        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, [mapStyle]);

    // Update map with establishment data when data changes
    useEffect(() => {
        if (!mapInstance.current) return;

        const map = mapInstance.current;

        // Wait for map to be ready
        if (map.isStyleLoaded()) {
            addEstablishmentsToMap(map, establishmentData);
        } else {
            map.on('load', () => {
                addEstablishmentsToMap(map, establishmentData);
            });
        }
    }, [establishmentData]);

    const getProportionalSize = (maxCost, cost, size) => {
        if (maxCost === 0) return 0;
        return (cost / maxCost) * size;
    };

    // Helper function to create pie chart as canvas image
    const createPieChartImage = (maxCost, cost, size = 40) => {
        const finalSize = getProportionalSize(maxCost, cost, size)
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // Clear canvas with white background (no transparency)
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);

        // if (!fuelTypes || fuelTypes.length === 0) {
        //     // Draw a simple gray circle if no fuel data
        //     ctx.beginPath();
        //     ctx.arc(size / 2, size / 2, size / 2 - 2, 0, 2 * Math.PI);
        //     ctx.fillStyle = '#CCCCCC';
        //     ctx.fill();
        //     ctx.strokeStyle = '#999999';
        //     ctx.lineWidth = 2;
        //     ctx.stroke();
        //     return canvas.toDataURL('image/png');
        // }

        // const total = fuelTypes.reduce(
        //     (sum, fuel) => sum + (fuel.consumption || 0),
        //     0
        // );

        const total = cost;
        if (total === 0) {
            // Draw a simple gray circle if no consumption data
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, size / 2 - 2, 0, 2 * Math.PI);
            ctx.fillStyle = '#CCCCCC';
            ctx.fill();
            ctx.strokeStyle = '#999999';
            ctx.lineWidth = 2;
            ctx.stroke();
            return canvas.toDataURL('image/png');
        }

        const centerX = size / 2;
        const centerY = size / 2;
        const radius = size / 2 - 2;
        let currentAngle = -Math.PI / 2; // Start from top

        // [{consumption: cost}].forEach((fuel, index) => {
            const percentage = (cost || 0) / total;
            const angle = percentage * 2 * Math.PI;

            if (angle > 0) {
                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.arc(
                    centerX,
                    centerY,
                    radius,
                    currentAngle,
                    currentAngle + angle
                );
                ctx.closePath();
                ctx.fillStyle =
                    ECHARTS_COLOR_PALETTE[0 % ECHARTS_COLOR_PALETTE.length];
                ctx.fill();
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 1;
                ctx.stroke();

                currentAngle += angle;
            }
        // });

        // Draw outer border
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.strokeStyle = '#333333';
        ctx.lineWidth = 2;
        ctx.stroke();

        return [canvas.toDataURL('image/png'), finalSize];
    };

    // Helper function to add establishments to map
    const addEstablishmentsToMap = (map, data) => {

        const maxTotalPrice = Math.max(...infoConsumptions.map(e => e.total_price));

        // Clear existing markers
        const existingMarkers = document.querySelectorAll(
            '.establishment-marker'
        );
        existingMarkers.forEach(marker => marker.remove());

        if (infoConsumptions.length === 0) {
            return;
        }

        // Filter valid establishments
        const validEstablishments = data.filter(
            establishment =>
                establishment.establishment_latitude &&
                establishment.establishment_longitude &&
                !isNaN(establishment.establishment_latitude) &&
                !isNaN(establishment.establishment_longitude)
        );

        // Create markers for each establishment
        infoConsumptions.forEach(establishment => {
            const establishmentData = infoEstablishments.find(est => est.name === establishment._id)

            // Create marker element
            const markerElement = document.createElement('div');
            markerElement.className = 'establishment-marker';
            markerElement.className = "establishment-point-marker";

            // Get pointSize
            const pointSize = calculatePointSize(
                establishment.total_price,
                maxTotalPrice,
                10
            )

            Object.assign(
                markerElement.style, 
                {
                    width: `${pointSize}px`,
                    height: `${pointSize}px`,
                    backgroundColor: ECHARTS_COLOR_PALETTE[0],
                    borderRadius: "50%",
                    border: "1px solid white",
                    boxShadow: "0 0 4px rgba(0,0,0,0.3)",
                    cursor: "pointer",
                }
            );

            // Create marker
            const marker = new mapboxgl.Marker(markerElement)
                .setLngLat([
                    establishmentData?.longitude,
                    establishmentData?.latitude,
                ])
                .addTo(map);
            
            // Add coords to boundsRef
            boundsRef.current
                .extend([
                    establishmentData?.longitude,
                    establishmentData?.latitude
                ]);

            // Fit the map to show all added points
            mapInstance.current.fitBounds(boundsRef.current, { padding: 15, maxZoom: 14 });
            
            

            // Add click event
            markerElement.addEventListener('click', e => {
                e.stopPropagation();

                // Create compact tooltip content
                const popupContent = `
                    <div style="padding: 8px 12px; min-width: 180px; max-width: 250px;">
                        <div style="font-weight: 600; font-size: 14px; color: #2c3e50; margin-bottom: 6px; line-height: 1.2;">
                            ${establishmentData?.name || 'Establecimiento sin nombre'}
                        </div>
                        <div style="font-size: 12px; color: #7f8c8d; margin-bottom: 4px;">
                            Total US$: <span style="font-weight: 600; color: #e74c3c;">${(
                                establishment?.total_price || 0
                            ).toLocaleString()}</span>
                        </div>
                    </div>
                `;

                // Close any existing popup first
                const existingPopup = document.querySelector('.mapboxgl-popup');
                if (existingPopup) {
                    existingPopup.remove();
                }

                const popup = new mapboxgl.Popup({
                    closeButton: true,
                    closeOnClick: true,
                    closeOnMove: false,
                    offset: 15,
                    className: 'establishment-popup',
                    maxWidth: '300px',
                })
                    .setLngLat([
                        establishmentData?.longitude,
                        establishmentData?.latitude,
                    ])
                    .setHTML(popupContent)
                    .addTo(map);

                // Add event listener for close button after popup is rendered
                setTimeout(() => {
                    const closeButton = document.querySelector(
                        '.establishment-popup .mapboxgl-popup-close-button'
                    );
                    if (closeButton) {
                        closeButton.addEventListener('click', event => {
                            event.stopPropagation();
                            popup.remove();
                        });
                    }
                }, 100);
            });
        });
    };

    const actions = (
        <div className='btn-box-dashboard' style={{ display: 'flex', gap: 8 }}>
            <Select
                placeholder='Fecha'
                data={[
                    { label: 'Ciclo', value: 'ciclo' },
                    { label: 'Año', value: 'año' },
                    { label: 'Semestre', value: 'semestre' },
                    { label: 'Mes', value: 'mes' },
                    { label: 'Día', value: 'dia' },
                ]}
                value={selectedDate}
                onChange={value => value && setSelectedDate(value)}
                size='xs'
                radius='lg'
                styles={{ input: { width: 100 } }}
            />
        </div>
    );

    // Add custom styles for the popup
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .establishment-popup .mapboxgl-popup-content {
                padding: 0 !important;
                border-radius: 8px !important;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
                border: 1px solid #e1e8ed !important;
            }
            .establishment-popup .mapboxgl-popup-tip {
                border-top-color: #e1e8ed !important;
            }
            .establishment-popup .mapboxgl-popup-close-button {
                font-size: 18px !important;
                color: #7f8c8d !important;
                right: 8px !important;
                top: 6px !important;
                width: 24px !important;
                height: 24px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                border-radius: 50% !important;
                background: rgba(255, 255, 255, 0.9) !important;
                z-index: 1000 !important;
                cursor: pointer !important;
                transition: all 0.2s ease !important;
            }
            .establishment-popup .mapboxgl-popup-close-button:hover {
                color: #e74c3c !important;
                background: rgba(231, 76, 60, 0.1) !important;
                transform: scale(1.1) !important;
            }
            .establishment-popup .mapboxgl-popup-close-button:active {
                transform: scale(0.95) !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    return (
        <OChartCard
            title={title}
            actions={actions}
            isLoading={infoConsumptions?.isLoading && !hasLoadedOnce}
            error={infoConsumptions?.error}
            isEmpty={noData}
            emptyIconClass='ri-time-line'
            emptyMessage='No cuenta con registro de consumo de combustible'
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
            {!noData && (
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        ref={mapContainer}
                        style={{
                            width: '100%',
                            height: '100%',
                            flex: 1,
                            minHeight: minHeight || '400px',
                            overflow: 'hidden',
                        }}
                    />
                </div>
            )}
        </OChartCard>
    );
};
